/**
 * ContractorOfferHistoryPanel — dispatcher-facing accountability surface.
 *
 * Read-only. Renders canonical metrics from getContractorOfferMetrics +
 * decline-reason distribution + offer history list. NEVER computes rates
 * inline; always reads through the resolver. Time window selectable
 * (Lifetime / 90d).
 *
 * Financial visibility: shows payoutAmountSnapshot only. NEVER reads
 * internal pricing or any internal pricing.
 */

import { useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import {
  getContractorOfferMetrics,
  getContractorOfferHistory,
  getDeclineReasonDistribution,
  MIN_SAMPLE_SIZE,
} from "@/data/contractor/offerMetrics";
import { OFFER_DECLINE_REASONS } from "@/data/contractor/marketplaceOffer";
import type { MarketplaceOffer } from "@/data/contractor/marketplaceOffer";
import { P2_EMPTY_STATE_LABEL } from "@/data/contractor/p2Activation";
import { Card, CardContent } from "@/components/ui/card";
import { History, Info } from "lucide-react";

interface Props {
  contractorId: string;
}

type WindowKey = "lifetime" | "90d";

const DECLINE_LABELS: Record<string, string> = Object.fromEntries(
  OFFER_DECLINE_REASONS.map((r) => [r.value, r.label]),
);

function formatPct(v: number | null): string {
  return v == null ? "—" : `${Math.round(v * 100)}%`;
}
function formatMs(v: number | null): string {
  if (v == null) return "—";
  const minutes = Math.round(v / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  return `${hours}h`;
}

export function ContractorOfferHistoryPanel({ contractorId }: Props) {
  const { marketplaceOffers, jobs, systemEvents, contractors } = useAppData();
  const [windowKey, setWindowKey] = useState<WindowKey>("lifetime");

  const since = useMemo(() => {
    if (windowKey === "lifetime") return undefined;
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d;
  }, [windowKey]);

  const normalized = (value?: string) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const currentContractor = useMemo(
    () => contractors.find((c) => c.id === contractorId),
    [contractors, contractorId],
  );

  const canonicalOffers = useMemo(() => {
    if (!currentContractor) return marketplaceOffers;
    const targetName = normalized(currentContractor.companyName || currentContractor.contactName);
    if (!targetName) return marketplaceOffers;

    const aliasIds = new Set(
      contractors
        .filter((c) => {
          const sameName =
            normalized(c.companyName || c.contactName) === targetName;
          return sameName;
        })
        .map((c) => c.id),
    );
    aliasIds.add(contractorId);

    return marketplaceOffers.map((offer) =>
      aliasIds.has(offer.contractorId) && offer.contractorId !== contractorId
        ? { ...offer, contractorId }
        : offer,
    );
  }, [contractors, contractorId, currentContractor, marketplaceOffers]);

  const metrics = useMemo(
    () =>
      getContractorOfferMetrics(contractorId, canonicalOffers, jobs, systemEvents, { since }),
    [contractorId, canonicalOffers, jobs, systemEvents, since],
  );
  const distribution = useMemo(
    () => getDeclineReasonDistribution(contractorId, canonicalOffers, { since }),
    [contractorId, canonicalOffers, since],
  );
  const history = useMemo(
    () => getContractorOfferHistory(contractorId, canonicalOffers, { since }),
    [contractorId, canonicalOffers, since],
  );

  const resolvedHistory = useMemo(() => {
    const byJob = new Map<string, MarketplaceOffer[]>();
    for (const offer of history) {
      const bucket = byJob.get(offer.jobId) ?? [];
      bucket.push(offer);
      byJob.set(offer.jobId, bucket);
    }

    const precedence: Record<MarketplaceOffer["status"], number> = {
      accepted: 6,
      declined: 5,
      expired: 4,
      withdrawn: 3,
      superseded: 2,
      pending: 1,
    };

    const effectiveStatus = (offer: MarketplaceOffer): MarketplaceOffer["status"] => {
      const audit = Array.isArray(offer.audit) ? offer.audit : [];
      const hasAccepted = audit.some((a) => a?.event === "offer_accepted");
      if (hasAccepted) return "accepted";
      const hasDeclined = audit.some((a) => a?.event === "offer_declined");
      if (hasDeclined) return "declined";
      const hasExpired = audit.some((a) => a?.event === "offer_expired");
      if (hasExpired) return "expired";
      return offer.status;
    };

    const pickBest = (offers: MarketplaceOffer[]): MarketplaceOffer => {
      return [...offers].sort((a, b) => {
        const pa = precedence[effectiveStatus(a)] ?? 0;
        const pb = precedence[effectiveStatus(b)] ?? 0;
        if (pb !== pa) return pb - pa;
        const ta = Date.parse(a.respondedAt ?? a.sentAt);
        const tb = Date.parse(b.respondedAt ?? b.sentAt);
        return tb - ta;
      })[0];
    };

    return Array.from(byJob.values())
      .map(pickBest)
      .sort((a, b) => {
        const ta = Date.parse(a.respondedAt ?? a.sentAt);
        const tb = Date.parse(b.respondedAt ?? b.sentAt);
        return tb - ta;
      });
  }, [history]);

  const insufficient = metrics.sampleSize < MIN_SAMPLE_SIZE;
  const jobsById = useMemo(() => new Map(jobs.map((job) => [job.id, job])), [jobs]);

  const getOfferJobLabel = (jobId: string): string => {
    const job = jobsById.get(jobId);
    if (!job) return jobId;
    const maybeJobNumber = (job as { jobNumber?: unknown }).jobNumber;
    if (typeof maybeJobNumber === "string" && maybeJobNumber.trim().length > 0) {
      return maybeJobNumber.trim();
    }
    return job.id;
  };

  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
            <History className="h-4 w-4" /> Offer History
          </h2>
          <div className="flex items-center gap-1 rounded-full ring-1 ring-border p-0.5">
            {(["lifetime", "90d"] as WindowKey[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setWindowKey(k)}
                className={`px-2 py-0.5 text-[10px] font-semibold rounded-full transition ${
                  windowKey === k
                    ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {k === "lifetime" ? "Lifetime" : "90d"}
              </button>
            ))}
          </div>
        </div>

        {resolvedHistory.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground flex items-start gap-2">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>{P2_EMPTY_STATE_LABEL}</span>
          </div>
        ) : (
          <>
            {insufficient && (
              <div className="mb-3 rounded-md bg-muted/40 px-2 py-1.5 text-[11px] text-muted-foreground flex items-center gap-1.5">
                <Info className="h-3 w-3" />
                Insufficient data — need {MIN_SAMPLE_SIZE - metrics.sampleSize} more
                terminal offer{MIN_SAMPLE_SIZE - metrics.sampleSize === 1 ? "" : "s"} to
                show rates.
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
              <Stat label="Acceptance" value={formatPct(metrics.acceptanceRate)} />
              <Stat label="Decline" value={formatPct(metrics.declineRate)} />
              <Stat label="Expiry" value={formatPct(metrics.expiryRate)} />
              <Stat label="Median response" value={formatMs(metrics.medianResponseTimeMs)} />
              <Stat
                label="Post-accept reassign"
                value={formatPct(metrics.postAcceptReassignmentRate)}
              />
              <Stat
                label="Sample"
                value={`${metrics.sampleSize} of ${
                  metrics.sampleSize + metrics.withdrawnCount + metrics.supersededCount
                }`}
              />
            </div>

            {distribution && (
              <div className="mb-3 border-t pt-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Decline reasons
                </p>
                <div className="space-y-1">
                  {Object.entries(distribution)
                    .sort((a, b) => b[1] - a[1])
                    .map(([reason, pct]) => (
                      <div key={reason} className="flex items-center gap-2 text-[11px]">
                        <span className="flex-1 truncate">
                          {DECLINE_LABELS[reason] ?? reason}
                        </span>
                        <span className="font-mono text-muted-foreground">
                          {Math.round(pct)}%
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            <div className="border-t pt-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                Recent offers
              </p>
              <ul className="space-y-1">
                {resolvedHistory.slice(0, 8).map((o) => {
                  const audit = Array.isArray(o.audit) ? o.audit : [];
                  const uiStatus: MarketplaceOffer["status"] = audit.some((a) => a?.event === "offer_accepted")
                    ? "accepted"
                    : audit.some((a) => a?.event === "offer_declined")
                      ? "declined"
                      : audit.some((a) => a?.event === "offer_expired")
                        ? "expired"
                        : o.status;
                  return (
                  <li
                    key={o.id}
                    className="flex items-center justify-between gap-2 text-[11px] py-0.5"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="font-mono text-muted-foreground">{getOfferJobLabel(o.jobId)}</span>
                      <span className="ml-2 capitalize">{uiStatus}</span>
                      {o.responseReason && (
                        <span className="ml-2 text-muted-foreground">
                          · {DECLINE_LABELS[o.responseReason] ?? o.responseReason}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {new Date(o.sentAt).toLocaleDateString()}
                    </span>
                  </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-background/40 px-2 py-1.5">
      <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="text-sm font-bold tabular-nums">{value}</p>
    </div>
  );
}
